/**
 * Хук для получения конфигурации tenant с информацией о подключенных мессенджерах
 * Дата: 05.02.2026 - TODO-055: Компонент для неподключенных мессенджеров
 * Дата: 07.02.2026 - FIX: snake_case ключи из API вместо PascalCase
 *                    FIX: убрана проверка webhook_secret (настраивается на уровне сервера)
 * 
 * СВЯЗАННЫЕ МОДУЛИ:
 * - api/client.ts - API клиент
 * - components/features/messages/MessengerNotConnected.tsx - компонент "Мессенджер не подключен"
 * - components/features/messages/InitiateConversationModal.tsx - фильтрация каналов
 * - components/features/messages/ChatWindow.tsx - отображение статуса мессенджера
 * 
 * КАК РАБОТАЕТ:
 * - Получает конфиг tenant через API /admin/tenants/:id
 * - API возвращает snake_case ключи: wazzup_api_key, telegram_bot_token и т.д.
 * - Возвращает объект с флагами для каждого мессенджера
 * - Используется для фильтрации каналов и отображения "не подключен"
 * 
 * ФОРМАТ CONFIG ИЗ API (snake_case):
 *   wazzup_api_key, wazzup_channel_id, wazzup_waba_channel_id,
 *   telegram_bot_token, telegram_bot_username,
 *   telegram_client_enabled, telegram_client_phone, telegram_client_status,
 *   max_bot_token, max_bot_username, max_enabled,
 *   sms_enabled, sms_login, sms_api_key, sms_sender_id
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

// Дата: 07.02.2026 - snake_case ключи, как приходят из API
export interface TenantConfigData {
  // WhatsApp (Wazzup)
  wazzup_api_key?: string;
  wazzup_channel_id?: string;        // Regular WhatsApp
  wazzup_waba_channel_id?: string;   // WABA WhatsApp
  whatsapp_provider?: string;
  
  // Telegram
  telegram_bot_token?: string;
  telegram_bot_username?: string;
  telegram_client_enabled?: boolean;
  telegram_client_phone?: string;
  telegram_client_status?: string;
  
  // MAX
  max_bot_token?: string;
  max_bot_username?: string;
  max_enabled?: boolean;
  max_client_enabled?: boolean;
  max_client_phone?: string;
  max_client_status?: string;
  
  // SMS (Intis)
  sms_enabled?: boolean;
  sms_login?: string;
  sms_api_key?: string;
  sms_sender_id?: string;
  sms_tel_enabled?: boolean;
  sms_bridge_tenant_id?: string;
  sms_bridge_tenant_slug?: string;
  
  // Channel Priority
  delivery_settings?: {
    channel_priority?: string[];
    disabled_channels?: string[];
  };
  
  // Прочее
  broadcasts_enabled?: boolean;
  manual_broadcasts_enabled?: boolean;
  auto_reply_enabled?: boolean;
  datalayer_api_url?: string;
  datalayer_api_key?: string;
  [key: string]: unknown; // Для доп. полей
}

export interface MessengerStatus {
  whatsapp_waba: boolean;
  whatsapp_regular: boolean;
  telegram_bot: boolean;
  telegram_client: boolean;
  max: boolean;
  max_client: boolean;
  sms: boolean;
  sms_tel: boolean;
}

export function useTenantConfig(tenantId?: string) {
  const query = useQuery({
    queryKey: ['tenant-config', tenantId],
    queryFn: async (): Promise<TenantConfigData | null> => {
      if (!tenantId) return null;
      try {
        // 15.02.2026 — FIX: сначала пробуем /tenant/config (доступен для всех ролей с settings:read),
        // fallback на /admin/tenants/:id (только SuperAdmin) если первый не вернул config
        try {
          const response = await apiClient.get('/tenant/config', {
            params: { tenant_id: tenantId },
          });
          if (response.data?.config) return response.data.config;
          if (response.data) return response.data;
        } catch {
          // Если /tenant/config недоступен (например SuperAdmin без tenant_id в JWT),
          // пробуем admin endpoint
        }
        const response = await apiClient.get(`/admin/tenants/${tenantId}`);
        return response.data.config || response.data;
      } catch (error) {
        console.error('Failed to fetch tenant config:', error);
        return null;
      }
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 минут
  });

  // Дата: 07.02.2026 - Определяем статус подключения с правильными snake_case ключами
  const getMessengerStatus = (): MessengerStatus => {
    const config = query.data;
    
    return {
      // WABA: нужен API ключ Wazzup + ID WABA канала
      whatsapp_waba: !!(config?.wazzup_api_key && config?.wazzup_waba_channel_id),
      // Regular: нужен API ключ Wazzup + ID Regular канала
      whatsapp_regular: !!(config?.wazzup_api_key && config?.wazzup_channel_id),
      // TG Bot: достаточно наличия токена (webhook_secret на уровне сервера)
      telegram_bot: !!(config?.telegram_bot_token),
      // TG Client: должен быть включён
      telegram_client: !!(config?.telegram_client_enabled),
      // MAX Bot: токен + не отключён
      max: !!(config?.max_bot_token && config?.max_enabled !== false),
      // MAX Client: клиентский аккаунт клиники
      max_client: !!(config?.max_client_enabled && config?.max_client_status === 'connected'),
      // SMS: включён + есть логин
      sms: !!(config?.sms_enabled && config?.sms_login),
      sms_tel: !!(config?.sms_tel_enabled && (config?.sms_bridge_tenant_id || config?.sms_bridge_tenant_slug)),
    };
  };

  // Получить список подключенных каналов
  const getConnectedChannels = (): string[] => {
    const status = getMessengerStatus();
    return Object.entries(status)
      .filter(([, connected]) => connected)
      .map(([channel]) => channel);
  };

  // Проверить подключен ли конкретный канал
  const isChannelConnected = (channel: string): boolean => {
    const status = getMessengerStatus();
    return status[channel as keyof MessengerStatus] ?? false;
  };

  return {
    config: query.data,
    isLoading: query.isLoading,
    error: query.error,
    messengerStatus: getMessengerStatus(),
    connectedChannels: getConnectedChannels(),
    isChannelConnected,
    refetch: query.refetch,
  };
}
